/**
 * PetroNexus — Company Store
 *
 * Persists company-level data (logo URL, name, SARS settings) across sessions.
 * The 3D scene uses logoUrl for truck decals; InvoicePrintPage uses vatNumber.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

interface CompanyState {
  companyName: string;
  logoUrl: string;
  sector: string;         // 'fuel_seller' | 'logistics' | 'mining' | 'agriculture' | 'construction'
  vatNumber: string;      // SARS VAT registration number (10 digits, starts with 4)
  accountantEmail: string; // External bookkeeper — receives daily transaction CSV at 07:00
  companyPhone: string;
  companyAddress: string;
  checklistDismissedAt: string | null; // ISO timestamp — set when owner dismisses onboarding checklist
  /**
   * DA 66 Platform Fee percentage (LOCKED policy, CLAUDE.md §20 — Apr 2026).
   * Default 0.25 (25%) for Starter + Professional. Enterprise-only override down to
   * 0.20 (20%) floor. Server CHECK constraint enforces the floor.
   * Null = not yet loaded; caller falls back to PETRONEXUS_FEE_PCT from sarsRates.
   */
  da66ServiceFeePct: number | null;
  setLogoUrl: (url: string) => void;
  setCompanyName: (name: string) => void;
  /** Fetch from /tenants/mine and sync all fields into store */
  loadCompany: () => Promise<void>;
  /** Persist SARS compliance settings to the backend */
  updateComplianceSettings: (vatNumber: string, accountantEmail: string) => Promise<void>;
  /** Dismiss the onboarding checklist permanently (OWNER only) */
  dismissOnboardingChecklist: () => Promise<void>;
}

const PETRONEXUS_FAVICON_HREF = '/favicon-32.png';
const PETRONEXUS_APP_ICON_HREF = '/pwa-192x192.png';

/** Helper — syncs document.title while keeping browser chrome on PetroNexus branding */
function syncBrowserChrome(name: string) {
  document.title = name
    ? `${name} — PetroNexus`
    : 'PetroNexus — Fuel Distribution Management';

  document.querySelectorAll<HTMLLinkElement>("link[rel='icon']").forEach((el) => {
    el.href = el.sizes?.contains('192x192') ? PETRONEXUS_APP_ICON_HREF : PETRONEXUS_FAVICON_HREF;
    el.type = 'image/png';
  });
}

export const useCompanyStore = create<CompanyState>()(
  persist(
    (set) => ({
      companyName: '',
      logoUrl: '',
      sector: 'fuel_seller',
      vatNumber: '',
      accountantEmail: '',
      companyPhone: '',
      companyAddress: '',
      checklistDismissedAt: null,
      da66ServiceFeePct: null,

      setLogoUrl: (url) => {
        set({ logoUrl: url });
        const state = useCompanyStore.getState();
        syncBrowserChrome(state.companyName);
      },
      setCompanyName: (name) => {
        set({ companyName: name });
        syncBrowserChrome(name);
      },

      loadCompany: async () => {
        try {
          const { data } = await api.get('/tenants/mine');
          const name    = data.name            ?? '';
          const logo    = data.logoUrl         ?? '';
          const sector  = data.industry        ?? 'fuel_seller';
          const vatNum  = data.vatNumber       ?? '';
          const acctEmail = data.accountantEmail ?? '';
          const phone   = data.phone           ?? '';
          const address = data.address         ?? '';
          const checklistDismissed = data.onboardingChecklistDismissedAt ?? null;
          // numeric(4,3) column — Postgres returns it as string via TypeORM; normalise.
          const feePctRaw = data.da66_service_fee_pct ?? data.da66ServiceFeePct ?? null;
          const feePct = feePctRaw === null || feePctRaw === undefined ? null : Number(feePctRaw);
          set({ companyName: name, logoUrl: logo, sector, vatNumber: vatNum, accountantEmail: acctEmail, companyPhone: phone, companyAddress: address, checklistDismissedAt: checklistDismissed, da66ServiceFeePct: Number.isFinite(feePct) ? feePct : null });
          syncBrowserChrome(name);
        } catch {
          // Silently ignore — user may not be logged in yet
        }
      },

      updateComplianceSettings: async (vatNumber, accountantEmail) => {
        try {
          await api.patch('/tenants/mine', { vatNumber, accountantEmail });
          set({ vatNumber, accountantEmail });
        } catch (err: unknown) {
          const e = err as { response?: { data?: { message?: string } }; message?: string };
          throw new Error(e?.response?.data?.message ?? 'Failed to save compliance settings');
        }
      },

      dismissOnboardingChecklist: async () => {
        try {
          await api.patch('/tenants/mine/checklist-dismiss');
          set({ checklistDismissedAt: new Date().toISOString() });
        } catch {
          // Silently ignore — checklist dismiss is best-effort
        }
      },
    }),
    {
      name: 'petronexus-company',
      onRehydrateStorage: () => (state) => {
        if (state) {
          syncBrowserChrome(state.companyName);
        }
      },
    }
  )
);
